import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzGridDsConfig } from "@template-ui/types";
import { ColDef, GridApi, GridOptions, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { TooltipModule } from "primeng/tooltip";
import { RoleService } from "@app/shared/services/roles.service";
import { createTerminologyGridHeader } from "@app/shared/terminology/terminology-grid-header";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { forkJoin, Subscription } from "rxjs";
import {
  PlatformAccessAssignmentRevokeRequest,
  PlatformAccessAssignmentsDetail,
  PlatformAccessAdminDisplayError,
  PlatformPermissionCatalogItem,
  PlatformPermissionGrant,
  PlatformPermissionGrantCreateRequest,
  PlatformRoleCatalogItem,
  PlatformRoleAssignment,
  PlatformRoleAssignmentCreateRequest,
  PlatformUserCatalogRow,
  PlatformUserCreated,
  PlatformUserEffectivePermissions,
  PlatformUserProfileUpdateRequest,
} from "./platform-access-admin.models";
import {
  PlatformAccessAdminService,
  mapPlatformAccessAdminError,
} from "./platform-access-admin.service";
import { PLATFORM_ACCESS_TERMINOLOGY } from "./platform-access.terminology";
import { PlatformAccessUserCreatePanelComponent } from "./platform-access-user-create-panel.component";
import {
  EffectivePermissionTarget,
  EffectivePermissionToggle,
  PlatformAccessUserEffectivePermissionsComponent,
} from "./platform-access-user-effective-permissions.component";

const PLATFORM_ACCESS_ADMIN_AUTHOR = "platform_access_admin.author";
const PLATFORM_ACCESS_ADMIN_ROLLBACK = "platform_access_admin.rollback";
const GLOBAL_SCOPE_TYPE_CODE = "global";
type AccessAssignmentTab = "roles" | "permissions";

type UserProfileTextField = "displayName" | "email" | "phoneNumber" | "jobTitle" | "department";

@Component({
  selector: "phms-platform-access-roles-permissions",
  imports: [
    CommonModule,
    UiLibraryModule,
    TooltipModule,
    TerminologyIdentityDirective,
    PlatformAccessUserCreatePanelComponent,
    PlatformAccessUserEffectivePermissionsComponent,
  ],
  templateUrl: "./platform-access-roles-permissions.component.html",
  styleUrl: "./platform-access-roles-permissions.component.scss",
})
export class PlatformAccessRolesPermissionsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformAccessAdminService = inject(PlatformAccessAdminService);
  private readonly roles = inject(RoleService);
  private readonly injector = inject(Injector);
  private readonly createUserButton = viewChild<ElementRef<HTMLButtonElement>>("createUserButton");
  private readonly detailPanel = viewChild<ElementRef<HTMLElement>>("detailPanel");
  private assignmentsLoadSubscription?: Subscription;
  private assignmentsLoadRequestId = 0;
  private userGridApi?: GridApi<PlatformUserCatalogRow>;
  private pendingUserSelectionId: number | null = null;

  /**
   * The page's vocabulary (ADO #1542): every visible string resolves through one of these usages,
   * so a published Terminology edit reaches the headings, labels, buttons, grid columns, tabs and
   * messages while technical identifiers stay literal.
   */
  readonly usage = PLATFORM_ACCESS_TERMINOLOGY.accessManagement;
  readonly text = createTerminologyUsageSignals(this.usage);
  private readonly userColumnHeader = createTerminologyGridHeader(this.usage.usersColumnUser, this.text.usersColumnUser);
  private readonly emailColumnHeader = createTerminologyGridHeader(this.usage.usersColumnEmail, this.text.usersColumnEmail);
  private readonly statusColumnHeader = createTerminologyGridHeader(this.usage.usersColumnStatus, this.text.usersColumnStatus);

  readonly canAuthor = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_AUTHOR);
  readonly canRollback = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_ROLLBACK);
  /**
   * True while the detail panel shows the new user form in place of the selected user or the
   * "Select a user" state. The selection and any unsaved detail edits are kept underneath, so
   * leaving create mode shows the panel exactly as it was.
   */
  readonly isCreatingUser = signal(false);
  readonly targetUserIdInput = signal("");
  readonly isAssignmentsLoading = signal(false);
  readonly isAssignmentSaving = signal(false);
  readonly isUserProfileSaving = signal(false);
  readonly activeActionKey = signal<string | null>(null);
  readonly userCatalogReloadTrigger = signal(0);
  readonly userSearch = signal("");
  readonly visibleUserCount = signal(0);
  readonly activeAssignmentTab = signal<AccessAssignmentTab>("roles");
  readonly roleFilter = signal("");
  readonly roleCatalog = signal<PlatformRoleCatalogItem[]>([]);
  readonly permissionCatalog = signal<PlatformPermissionCatalogItem[]>([]);
  readonly assignments = signal<PlatformAccessAssignmentsDetail | null>(null);
  readonly userProfileDraft = signal<PlatformUserProfileUpdateRequest | null>(null);
  readonly successMessage = signal<string | null>(null);
  readonly errorState = signal<PlatformAccessAdminDisplayError | null>(null);
  /** Bumped each time the selected user's assignments load, so the permissions view re-evaluates. */
  readonly assignmentsVersion = signal(0);
  /** Granted count reported by the permissions view for the selected user; null until it loads. */
  readonly effectivePermissionCount = signal<number | null>(null);

  readonly effectivePermissionCountLabel = computed(() => {
    const count = this.effectivePermissionCount();
    return count === null ? "–" : count.toString();
  });

  readonly hasUserProfileChanges = computed(() => {
    const current = this.assignments();
    const draft = this.userProfileDraft();
    return current !== null && draft !== null && (
      normalizeProfileValue(current.displayName) !== normalizeProfileValue(draft.displayName)
      || normalizeProfileValue(current.email) !== normalizeProfileValue(draft.email)
      || normalizeProfileValue(current.phoneNumber) !== normalizeProfileValue(draft.phoneNumber)
      || normalizeProfileValue(current.jobTitle) !== normalizeProfileValue(draft.jobTitle)
      || normalizeProfileValue(current.department) !== normalizeProfileValue(draft.department)
      || current.isActive !== draft.isActive
    );
  });

  readonly filteredRoles = computed(() => {
    const filter = normalizeSearch(this.roleFilter());
    return this.roleCatalog()
      .filter((role) => matchesCatalogFilter(role, filter))
      .sort((left, right) => compareAssignmentState(
        this.isRoleAssigned(left.code),
        this.isRoleAssigned(right.code),
        left.name,
        right.name,
      ));
  });

  readonly assignedRoleCount = computed(() =>
    new Set(
      (this.assignments()?.roleAssignments ?? [])
        .filter((assignment) => assignment.isActive)
        .map((assignment) => assignment.roleCode),
    ).size,
  );

  /**
   * The users grid follows the vocabulary: the column headings and the Status cell values are read
   * from the terminology signals, so the definitions (and the grid configuration built from them)
   * only change when a resolved text changes.
   */
  readonly userCatalogColumnDefs = computed<ColDef<PlatformUserCatalogRow>[]>(() => {
    const active = this.text.statusActive();
    const inactive = this.text.statusInactive();
    return [
      { field: "displayName", ...this.userColumnHeader(), flex: 1, minWidth: 150 },
      { field: "email", ...this.emailColumnHeader(), flex: 1.35, minWidth: 190 },
      {
        ...this.statusColumnHeader(),
        width: 92,
        valueGetter: ({ data }) => data?.isActive ? active : inactive,
      },
    ];
  });
  readonly userCatalogGridConfig = computed<EzGridDsConfig>(() => ({
    datasourceId: "platformUsers",
    bindings: [
      {
        bindingType: "READ",
        endpoint: this.platformAccessAdminService.getUsersODataEndpoint(),
        protocol: "GET_ODATA",
      },
    ],
    colDefs: this.userCatalogColumnDefs(),
  }));
  readonly userCatalogGridOptions: GridOptions<PlatformUserCatalogRow> = {
    paginationPageSize: 15,
    rowHeight: 38,
    headerHeight: 34,
    rowSelection: {
      mode: "singleRow",
      enableClickSelection: true,
      checkboxes: false,
    },
    getRowId: ({ data }) => data.id.toString(),
    onGridReady: (event) => this.onUserGridReady(event),
    onRowClicked: (event) => this.selectUser(event),
    onModelUpdated: (event) => {
      this.visibleUserCount.set(event.api.getDisplayedRowCount());
      this.selectPendingUserRow(event.api);
    },
  };

  constructor() {
    this.loadAccessCatalogs();
  }

  onUserSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.userSearch.set(value);
    this.userGridApi?.setGridOption("quickFilterText", value);
  }

  onRoleFilter(event: Event): void {
    this.roleFilter.set((event.target as HTMLInputElement).value);
  }

  onUserProfileInput(field: UserProfileTextField, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.userProfileDraft.update((draft) => draft ? { ...draft, [field]: value } : draft);
  }

  onUserProfileActiveChange(event: Event): void {
    const isActive = (event.target as HTMLInputElement).checked;
    this.userProfileDraft.update((draft) => draft ? { ...draft, isActive } : draft);
  }

  resetUserProfile(): void {
    const selectedUser = this.assignments();
    if (selectedUser) {
      this.userProfileDraft.set(createUserProfileDraft(selectedUser));
      this.clearMessages();
    }
  }

  saveUserProfile(): void {
    const selectedUser = this.assignments();
    const draft = this.userProfileDraft();
    if (!selectedUser || !draft) {
      this.ensureTargetUserLoaded();
      return;
    }

    if (!this.canAuthor()) {
      this.setAccessDenied(this.text.actionEditUserDetails());
      return;
    }

    if (!draft.displayName.trim()) {
      this.errorState.set({
        title: this.text.displayNameRequiredTitle(),
        messages: [this.text.displayNameRequiredMessage()],
        issueCodes: [],
      });
      return;
    }

    this.clearMessages();
    this.isUserProfileSaving.set(true);
    this.platformAccessAdminService.updateUserProfile(selectedUser.userId, {
      ...draft,
      displayName: draft.displayName.trim(),
      email: normalizeOptionalProfileValue(draft.email),
      phoneNumber: normalizeOptionalProfileValue(draft.phoneNumber),
      jobTitle: normalizeOptionalProfileValue(draft.jobTitle),
      department: normalizeOptionalProfileValue(draft.department),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedUser) => {
          this.assignments.set(updatedUser);
          this.userProfileDraft.set(createUserProfileDraft(updatedUser));
          this.isUserProfileSaving.set(false);
          this.userCatalogReloadTrigger.update((value) => value + 1);
          this.successMessage.set(this.text.profileSaved());
        },
        error: (error) => {
          this.isUserProfileSaving.set(false);
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  /**
   * Puts the detail panel into create mode: the new user form takes the place of the selected
   * user (or of the "Select a user" state) while the users grid stays in use beside it. The form
   * moves the focus to its first field once it has rendered.
   */
  startCreateUser(): void {
    if (!this.canAuthor()) {
      this.setAccessDenied(this.text.actionCreateUsers());
      return;
    }

    if (this.isCreatingUser()) {
      return;
    }

    this.clearMessages();
    this.isCreatingUser.set(true);
  }

  /**
   * Leaves create mode without creating a user. The panel shows what it showed before and the
   * focus returns to the Create user button the person started from.
   */
  cancelCreateUser(): void {
    this.isCreatingUser.set(false);
    this.focusAfterRender(this.createUserButton);
  }

  /**
   * Leaves create mode, reloads the users grid so the new row appears, selects it once the reload
   * lands, and loads the new user's assignments so the panel opens them for editing on the Roles
   * tab with the role they were created with ticked.
   */
  onUserCreated(user: PlatformUserCreated): void {
    this.isCreatingUser.set(false);
    this.pendingUserSelectionId = user.id;
    this.userCatalogReloadTrigger.update((value) => value + 1);
    this.activeAssignmentTab.set("roles");
    this.loadAssignments(user.id, `${user.displayName} ${this.text.userCreatedSuffix()}`);
    this.focusAfterRender(this.detailPanel);
  }

  formatLastAuthenticated(value: string | null | undefined): string {
    if (!value) {
      return this.text.profileLastSignInNever();
    }

    const timestamp = new Date(value);
    return Number.isNaN(timestamp.getTime())
      ? this.text.profileLastSignInUnavailable()
      : timestamp.toLocaleString();
  }

  selectAssignmentTab(tab: AccessAssignmentTab): void {
    this.activeAssignmentTab.set(tab);
  }

  onEffectivePermissionsLoaded(detail: PlatformUserEffectivePermissions): void {
    if (detail.userId === this.assignments()?.userId) {
      this.effectivePermissionCount.set(detail.permissions.length);
    }
  }

  /** A checkbox on the permissions view asks for an ad hoc grant or its revoke. */
  onPermissionToggle(event: EffectivePermissionToggle): void {
    this.togglePermission(event.permission, event.checked, event.revert);
  }

  getRoleTooltip(role: PlatformRoleCatalogItem): string {
    return `${role.code} — ${role.description || this.text.noDescription()}`;
  }

  getUserInitials(displayName: string): string {
    const names = displayName.trim().split(/\s+/).filter(Boolean);
    return names.slice(0, 2).map((name) => name[0]?.toUpperCase() ?? "").join("") || "U";
  }

  loadAssignments(userId = parsePositiveInteger(this.targetUserIdInput()), successMessage?: string): void {
    if (!userId) {
      this.errorState.set({
        title: this.text.targetUserRequiredTitle(),
        messages: [this.text.targetUserRequiredMessage()],
        issueCodes: [],
      });
      return;
    }

    this.targetUserIdInput.set(userId.toString());
    this.clearMessages();
    this.isAssignmentsLoading.set(true);
    if (userId !== this.assignments()?.userId) {
      this.effectivePermissionCount.set(null);
    }
    this.assignmentsLoadSubscription?.unsubscribe();
    const requestId = ++this.assignmentsLoadRequestId;

    this.assignmentsLoadSubscription = this.platformAccessAdminService
      .getAssignments(userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (assignments) => {
          if (requestId !== this.assignmentsLoadRequestId) {
            return;
          }

          this.assignments.set(assignments);
          this.userProfileDraft.set(createUserProfileDraft(assignments));
          this.isAssignmentsLoading.set(false);
          this.assignmentsVersion.update((version) => version + 1);
          if (successMessage) {
            this.successMessage.set(successMessage);
          }
        },
        error: (error) => {
          if (requestId !== this.assignmentsLoadRequestId) {
            return;
          }

          this.assignments.set(null);
          this.userProfileDraft.set(null);
          this.isAssignmentsLoading.set(false);
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  toggleRole(role: PlatformRoleCatalogItem, checked: boolean, source?: HTMLInputElement): void {
    if (!this.ensureTargetUserLoaded()) {
      return;
    }

    const activeAssignments = this.getActiveRoleAssignments(role.code);
    if (checked && activeAssignments.length === 0) {
      if (!this.canAuthor()) {
        this.setAccessDenied(this.text.actionAssignRoles());
        return;
      }

      const request: PlatformRoleAssignmentCreateRequest = {
        roleCode: role.code,
        scopeTypeCode: GLOBAL_SCOPE_TYPE_CODE,
        scopeKey: null,
        effectiveStartUtc: new Date().toISOString(),
        effectiveEndUtc: null,
      };
      this.runRoleAction(
        [this.platformAccessAdminService.assignRole(this.assignments()!.userId, request)],
        `${role.name} ${this.text.roleAssignedSuffix()}`,
        `role:assign:${role.code}`,
        () => this.restoreCheckbox(source, false),
      );
      return;
    }

    if (!checked && activeAssignments.length > 0) {
      if (!this.canRollback()) {
        this.setAccessDenied(this.text.actionRemoveRoles());
        return;
      }

      const revokeRequest = this.buildImmediateRevokeRequest();
      this.runRoleAction(
        activeAssignments.map((assignment) =>
          this.platformAccessAdminService.revokeRole(this.assignments()!.userId, assignment.id, revokeRequest),
        ),
        `${role.name} ${this.text.assignmentRemovedSuffix()}`,
        `role:revoke:${role.code}`,
        () => this.restoreCheckbox(source, true),
      );
    }
  }

  /**
   * Grants or revokes the ad hoc (direct, global) permission behind a checkbox. `revert` puts the
   * checkbox and its tile back when nothing is persisted: a failed request, a guard that stops the
   * change, or a click that matches what is already stored.
   */
  togglePermission(permission: EffectivePermissionTarget, checked: boolean, revert?: () => void): void {
    if (!this.ensureTargetUserLoaded()) {
      revert?.();
      return;
    }

    const activeGrants = this.getActivePermissionGrants(permission.code);
    if (checked && activeGrants.length === 0) {
      if (!this.canAuthor()) {
        this.setAccessDenied(this.text.actionGrantDirectPermissions());
        revert?.();
        return;
      }

      const request: PlatformPermissionGrantCreateRequest = {
        permissionCode: permission.code,
        scopeTypeCode: GLOBAL_SCOPE_TYPE_CODE,
        scopeKey: null,
        effectiveStartUtc: new Date().toISOString(),
        effectiveEndUtc: null,
      };
      this.runPermissionAction(
        [this.platformAccessAdminService.grantPermission(this.assignments()!.userId, request)],
        `${permission.name} ${this.text.permissionGrantedSuffix()}`,
        `permission:grant:${permission.code}`,
        () => revert?.(),
      );
      return;
    }

    if (!checked && activeGrants.length > 0) {
      if (!this.canRollback()) {
        this.setAccessDenied(this.text.actionRemoveDirectPermissions());
        revert?.();
        return;
      }

      const revokeRequest = this.buildImmediateRevokeRequest();
      this.runPermissionAction(
        activeGrants.map((grant) =>
          this.platformAccessAdminService.revokePermission(this.assignments()!.userId, grant.id, revokeRequest),
        ),
        `${permission.name} ${this.text.assignmentRemovedSuffix()}`,
        `permission:revoke:${permission.code}`,
        () => revert?.(),
      );
      return;
    }

    revert?.();
  }

  isRoleAssigned(roleCode: string): boolean {
    return this.getActiveRoleAssignments(roleCode).length > 0;
  }

  isPermissionAssigned(permissionCode: string): boolean {
    return this.getActivePermissionGrants(permissionCode).length > 0;
  }

  isRoleToggleDisabled(role: PlatformRoleCatalogItem): boolean {
    const isAssigned = this.isRoleAssigned(role.code);
    return this.isAssignmentsLoading()
      || this.isAssignmentSaving()
      || (isAssigned ? !this.canRollback() : !this.canAuthor());
  }

  isRoleBusy(roleCode: string): boolean {
    return this.activeActionKey()?.endsWith(`:${roleCode}`) ?? false;
  }

  trackByCode(_index: number, item: PlatformRoleCatalogItem | PlatformPermissionCatalogItem): string {
    return item.code;
  }

  private onUserGridReady(event: GridReadyEvent<PlatformUserCatalogRow>): void {
    this.userGridApi = event.api;
    if (this.userSearch()) {
      event.api.setGridOption("quickFilterText", this.userSearch());
    }
  }

  /** Opens the clicked user. A click made while the new user form is showing also leaves create mode. */
  private selectUser(event: RowClickedEvent<PlatformUserCatalogRow>): void {
    if (!event.data) {
      return;
    }

    this.isCreatingUser.set(false);
    if (event.data.id !== this.assignments()?.userId) {
      this.loadAssignments(event.data.id);
    }
  }

  private selectPendingUserRow(api: GridApi<PlatformUserCatalogRow>): void {
    const userId = this.pendingUserSelectionId;
    if (userId === null) {
      return;
    }

    const node = api.getRowNode(userId.toString());
    if (!node) {
      return;
    }

    this.pendingUserSelectionId = null;
    node.setSelected(true, true);
    api.ensureNodeVisible(node, "middle");
  }

  private loadAccessCatalogs(): void {
    forkJoin({
      roles: this.platformAccessAdminService.listRoles(),
      permissions: this.platformAccessAdminService.listPermissions(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ roles, permissions }) => {
          this.roleCatalog.set(roles);
          this.permissionCatalog.set(permissions);
        },
        error: (error) => {
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  private runRoleAction(
    requests: ReturnType<PlatformAccessAdminService["assignRole"]>[],
    successMessage: string,
    actionKey: string,
    restoreCheckbox: () => void,
  ): void {
    this.beginAction(actionKey);
    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.finishAction();
          this.loadAssignments(undefined, successMessage);
        },
        error: (error) => {
          this.finishAction();
          restoreCheckbox();
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  private runPermissionAction(
    requests: ReturnType<PlatformAccessAdminService["grantPermission"]>[],
    successMessage: string,
    actionKey: string,
    restoreCheckbox: () => void,
  ): void {
    this.beginAction(actionKey);
    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.finishAction();
          this.loadAssignments(undefined, successMessage);
        },
        error: (error) => {
          this.finishAction();
          restoreCheckbox();
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  private beginAction(actionKey: string): void {
    this.clearMessages();
    this.activeActionKey.set(actionKey);
    this.isAssignmentSaving.set(true);
  }

  private finishAction(): void {
    this.activeActionKey.set(null);
    this.isAssignmentSaving.set(false);
  }

  private restoreCheckbox(source: HTMLInputElement | undefined, checked: boolean): void {
    if (source) {
      source.checked = checked;
    }
  }

  private getActiveRoleAssignments(roleCode: string): PlatformRoleAssignment[] {
    return (this.assignments()?.roleAssignments ?? [])
      .filter((assignment) => assignment.isActive && assignment.roleCode === roleCode);
  }

  private getActivePermissionGrants(permissionCode: string): PlatformPermissionGrant[] {
    return (this.assignments()?.permissionGrants ?? [])
      .filter((grant) => grant.isActive && grant.permissionCode === permissionCode);
  }

  private buildImmediateRevokeRequest(): PlatformAccessAssignmentRevokeRequest {
    return { effectiveEndUtc: new Date().toISOString() };
  }

  private ensureTargetUserLoaded(): boolean {
    if (this.assignments()) {
      return true;
    }

    this.errorState.set({
      title: this.text.selectUserTitle(),
      messages: [this.text.selectUserMessage()],
      issueCodes: [],
    });
    return false;
  }

  private setAccessDenied(action: string): void {
    this.errorState.set({
      title: this.text.accessDeniedTitle(),
      messages: [`${this.text.accessDeniedPrefix()} ${action}.`],
      issueCodes: [],
    });
  }

  private clearMessages(): void {
    this.successMessage.set(null);
    this.errorState.set(null);
  }

  /** Moves the focus once the panel has re-rendered, so it lands on an element that is enabled and visible. */
  private focusAfterRender(target: () => ElementRef<HTMLElement> | undefined): void {
    afterNextRender(() => target()?.nativeElement.focus(), { injector: this.injector });
  }
}

function matchesCatalogFilter(
  item: PlatformRoleCatalogItem | PlatformPermissionCatalogItem,
  filter: string,
): boolean {
  if (!filter) {
    return true;
  }

  return [item.name, item.code, item.description]
    .some((value) => normalizeSearch(value).includes(filter));
}

function normalizeSearch(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase();
}

function compareAssignmentState(
  leftAssigned: boolean,
  rightAssigned: boolean,
  leftName: string,
  rightName: string,
): number {
  if (leftAssigned !== rightAssigned) {
    return leftAssigned ? -1 : 1;
  }

  return leftName.localeCompare(rightName);
}

function parsePositiveInteger(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function createUserProfileDraft(user: PlatformAccessAssignmentsDetail): PlatformUserProfileUpdateRequest {
  return {
    displayName: user.displayName,
    email: user.email ?? null,
    phoneNumber: user.phoneNumber ?? null,
    jobTitle: user.jobTitle ?? null,
    department: user.department ?? null,
    isActive: user.isActive,
  };
}

function normalizeProfileValue(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function normalizeOptionalProfileValue(value: string | null | undefined): string | null {
  const normalized = normalizeProfileValue(value);
  return normalized || null;
}
